import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import Icon from '../components/Icons';
import toast from 'react-hot-toast';
import { useProductos } from '../utils/useProductos';
import { Upload, Trash2, FileText, Eye, Download, X, Search } from 'lucide-react';

export default function HojasTecnicasPage() {
  const { productos: PRODUCTOS } = useProductos();
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [prodSel, setProdSel] = useState(null);
  const [showProdList, setShowProdList] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [buscarLista, setBuscarLista] = useState('');
  const fileRef = useRef();

  const load = async () => {
    try {
      const res = await api.get('/technical-sheets');
      setSheets(res.data);
    } catch { toast.error('Error al cargar hojas'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const codigosConHoja = new Set(sheets.map(s => s.productoCodigo));

  const sheetsFilt = sheets.filter(s =>
    !buscarLista ||
    s.productoCodigo?.toLowerCase().includes(buscarLista.toLowerCase()) ||
    s.productoNombre?.toLowerCase().includes(buscarLista.toLowerCase()) ||
    s.fileName?.toLowerCase().includes(buscarLista.toLowerCase())
  );

  const prodFilt = PRODUCTOS.filter(p =>
    !search ||
    p.codigo?.toLowerCase().includes(search.toLowerCase()) ||
    p.producto?.toLowerCase().includes(search.toLowerCase()) ||
    p.marca?.toLowerCase().includes(search.toLowerCase())
  );

  const handleUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Maximo 10MB por hoja tecnica'); return; }
    if (!prodSel) { toast.error('Selecciona un producto primero'); return; }

    const reader = new FileReader();
    reader.onload = async (ev) => {
      setUploading(true);
      try {
        await api.post('/technical-sheets', {
          productoCodigo: prodSel.codigo,
          productoNombre: prodSel.producto,
          fileName: file.name,
          fileData: ev.target.result,
          fileType: file.type,
        });
        toast.success(`Hoja tecnica de "${prodSel.producto}" subida correctamente`);
        setProdSel(null);
        setSearch('');
        await load();
      } catch { toast.error('Error al subir la hoja tecnica'); }
      finally { setUploading(false); }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleDelete = async (id, nombre) => {
    if (!window.confirm(`Eliminar hoja tecnica de "${nombre}"?`)) return;
    try {
      await api.delete(`/technical-sheets/${id}`);
      toast.success('Hoja tecnica eliminada');
      await load();
    } catch { toast.error('Error al eliminar'); }
  };

  const [viewing, setViewing] = useState(null); // { fileData, fileName, fileType, productoNombre }
  const [loadingView, setLoadingView] = useState(false);

  const handleView = async (id) => {
    setLoadingView(true);
    try {
      const res = await api.get(`/technical-sheets/${id}`);
      setViewing(res.data);
    } catch { toast.error('Error al cargar la hoja tecnica'); }
    finally { setLoadingView(false); }
  };

  const handleDownload = async (id, fileName) => {
    try {
      const res = await api.get(`/technical-sheets/${id}`);
      const link = document.createElement('a');
      link.href = res.data.fileData;
      link.download = res.data.fileName || fileName;
      link.click();
    } catch { toast.error('Error al descargar'); }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Hojas tecnicas</h1>
          <p className="page-subtitle">Sube una vez la hoja tecnica de cada producto y se adjuntara automaticamente en las cotizaciones</p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Subir nueva hoja */}
        <div className="card">
          <h3 className="card-title" style={{ marginBottom: 16 }}>Subir hoja tecnica</h3>

          <div className="form-group">
            <label className="form-label">1. Selecciona el producto</label>
            <div style={{ position: 'relative' }}>
              <input className="form-input" placeholder="Buscar producto por codigo o nombre..."
                value={search} onChange={e => { setSearch(e.target.value); setShowProdList(true); }}
                onFocus={() => setShowProdList(true)} />
              {showProdList && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #e5e5e5', borderRadius: 8, maxHeight: 240, overflowY: 'auto', zIndex: 100, boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}>
                  {prodFilt.slice(0, 20).map(p => (
                    <div key={p.codigo}
                      onClick={() => { setProdSel(p); setShowProdList(false); setSearch(`${p.codigo} - ${p.producto}`); }}
                      style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                      onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {codigosConHoja.has(p.codigo) && <span style={{ color: '#16a34a', fontSize: 12 }}>Tiene hoja</span>}
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#7B0D0D' }}>{p.codigo}</span>
                        <span style={{ fontSize: 13, fontWeight: 600 }}>{p.producto}</span>
                        <span style={{ fontSize: 11, color: '#888' }}>- {p.marca}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {prodSel && (
            <div style={{ padding: '10px 14px', background: '#f0f9ff', borderRadius: 8, border: '1px solid #bae6fd', marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: '#7B0D0D', fontWeight: 700 }}>{prodSel.codigo}</div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{prodSel.producto}</div>
              <div style={{ fontSize: 12, color: '#888' }}>{prodSel.marca} - {prodSel.presentacion}</div>
              {codigosConHoja.has(prodSel.codigo) && (
                <div style={{ fontSize: 12, color: '#d97706', marginTop: 6 }}>
                  Este producto ya tiene hoja tecnica. Al subir una nueva reemplazara la anterior.
                </div>
              )}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">2. Sube el archivo PDF</label>
            <div
              onClick={() => prodSel && fileRef.current.click()}
              style={{ border: '2px dashed #ccc', borderRadius: 12, padding: 32, textAlign: 'center', cursor: prodSel ? 'pointer' : 'not-allowed', background: prodSel ? '#fafafa' : '#f5f5f5', opacity: prodSel ? 1 : 0.6 }}
              onMouseEnter={e => prodSel && (e.currentTarget.style.borderColor = '#7B0D0D')}
              onMouseLeave={e => e.currentTarget.style.borderColor = '#ccc'}>
              <Upload size={32} style={{ color: '#ccc', marginBottom: 8 }} />
              <p style={{ color: '#666', fontSize: 14 }}>{uploading ? 'Subiendo...' : 'Clic para seleccionar PDF'}</p>
              <p style={{ color: '#aaa', fontSize: 12 }}>PDF - Maximo 10MB</p>
            </div>
            <input ref={fileRef} type="file" accept=".pdf,.PDF" style={{ display: 'none' }} onChange={handleUpload} />
          </div>
        </div>

        {/* Lista de hojas subidas */}
        <div className="card">
          <h3 className="card-title" style={{ marginBottom: 12 }}>Hojas tecnicas subidas ({sheets.length})</h3>
          {sheets.length > 0 && (
            <div style={{ position: 'relative', marginBottom: 14 }}>
              <Search size={15} style={{ position: 'absolute', left: 10, top: 10, color: '#aaa' }} />
              <input className="form-input" style={{ paddingLeft: 32 }}
                placeholder="Buscar hoja tecnica por codigo, nombre o archivo..."
                value={buscarLista} onChange={e => setBuscarLista(e.target.value)} />
            </div>
          )}
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner spinner-dark" style={{ width: 30, height: 30, margin: '0 auto' }} /></div>
          ) : sheets.length === 0 ? (
            <div className="empty-state">
              <FileText size={40} />
              <h3>Sin hojas tecnicas</h3>
              <p>Sube la primera hoja tecnica</p>
            </div>
          ) : sheetsFilt.length === 0 ? (
            <div className="empty-state">
              <FileText size={40} />
              <h3>Sin resultados</h3>
              <p>Ninguna hoja tecnica coincide con "{buscarLista}"</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {sheetsFilt.map(s => (
                <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: '#fafafa', borderRadius: 10, border: '1px solid #e5e5e5' }}>
                  <FileText size={20} style={{ color: '#7B0D0D', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#7B0D0D' }}>{s.productoCodigo}</div>
                    <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.productoNombre}</div>
                    <div style={{ fontSize: 11, color: '#888' }}>{s.fileName}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button className="btn-icon" title="Visualizar" onClick={() => handleView(s.id)} style={{ color: '#2563eb' }}>{loadingView ? <span className="spinner spinner-dark" style={{width:14,height:14}}/> : <Eye size={15} />}</button>
                    <button className="btn-icon" title="Descargar" onClick={() => handleDownload(s.id, s.fileName)}><Download size={15} /></button>
                    <button className="btn-icon" title="Eliminar" onClick={() => handleDelete(s.id, s.productoNombre)} style={{ color: '#dc2626' }}><Trash2 size={15} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── VISOR DE HOJA TÉCNICA ── */}
      {viewing && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.8)', zIndex:2000, display:'flex', flexDirection:'column' }}>
          {/* Header */}
          <div style={{ background:'#1a1a1a', padding:'12px 20px', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
            <div>
              <div style={{ color:'white', fontWeight:700, fontSize:15 }}>{viewing.productoNombre}</div>
              <div style={{ color:'#888', fontSize:12 }}>{viewing.productoCodigo} — {viewing.fileName}</div>
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => handleDownload(viewing.id, viewing.fileName)}>
                <Download size={14} /> Descargar
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => setViewing(null)} style={{ color:'#888' }}>
                <X size={14} /> Cerrar
              </button>
            </div>
          </div>
          {/* Contenido */}
          <div style={{ flex:1, overflow:'auto', display:'flex', justifyContent:'center', alignItems:'flex-start', padding:20 }}>
            {viewing.fileType === 'application/pdf' ? (
              <embed
                src={viewing.fileData}
                type="application/pdf"
                style={{ width:'100%', maxWidth:900, height:'calc(100vh - 120px)', borderRadius:8 }}
              />
            ) : (
              <img
                src={viewing.fileData}
                alt={viewing.productoNombre}
                style={{ maxWidth:'100%', maxHeight:'calc(100vh - 120px)', objectFit:'contain', borderRadius:8 }}
              />
            )}
          </div>
        </div>
      )}

      <div className="card" style={{ marginTop: 20 }}>
        <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Como funciona</h4>
        <div style={{ fontSize: 13, color: '#555', lineHeight: 1.8 }}>
          <p>1. Sube la hoja tecnica en PDF de cada producto una unica vez desde esta pagina.</p>
          <p>2. Al crear una cotizacion y seleccionar un producto, marca la casilla <strong>"Adjuntar hoja tecnica"</strong>.</p>
          <p>3. La hoja tecnica se indicara en la cotizacion. Al imprimir/generar el PDF, la referencia quedara incluida.</p>
          <p>4. Los productos que ya tienen hoja tecnica aparecen marcados en verde al buscarlos en cotizaciones.</p>
        </div>
      </div>
    </div>
  );
}
